import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, MapPin, Clock, DollarSign, Zap, Heart, 
  Target, TrendingUp, Rocket, Star, ArrowRight 
} from 'lucide-react';

const Careers: React.FC = () => {
  const openPositions = [
    {
      title: "Senior Full-Stack Developer",
      type: "Full-time",
      location: "Remote",
      salary: "$80K - $120K",
      description: "Build the next generation of AI-powered business tools",
      requirements: ["React/TypeScript", "Node.js", "AI/ML experience", "5+ years"],
      category: "Engineering"
    },
    {
      title: "Product Manager",
      type: "Full-time",
      location: "Remote",
      salary: "$70K - $100K",
      description: "Shape the future of entrepreneurial success tools",
      requirements: ["Product strategy", "User research", "Agile methodology", "3+ years"],
      category: "Product"
    },
    {
      title: "AI/ML Engineer",
      type: "Full-time",
      location: "Remote",
      salary: "$90K - $130K",
      description: "Develop cutting-edge AI validation algorithms",
      requirements: ["Python", "TensorFlow/PyTorch", "NLP", "MLOps", "4+ years"],
      category: "Engineering"
    },
    {
      title: "Growth Marketing Manager",
      type: "Full-time",
      location: "Remote",
      salary: "$60K - $90K",
      description: "Scale our user acquisition and retention strategies",
      requirements: ["Digital marketing", "Analytics", "A/B testing", "SaaS experience", "3+ years"],
      category: "Marketing"
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "Every line of code helps entrepreneurs succeed"
    },
    {
      icon: Zap,
      title: "Fast Execution",
      description: "Move quickly, learn faster, deliver results"
    },
    {
      icon: Heart,
      title: "Customer-First",
      description: "Build what users actually need and love"
    },
    {
      icon: Rocket,
      title: "Continuous Growth",
      description: "Personal and professional development always"
    }
  ];

  const benefits = [
    "Competitive salary and equity",
    "Remote-first culture",
    "Flexible working hours",
    "Health insurance",
    "Learning budget",
    "Team retreats",
    "Unlimited PTO",
    "Latest tech equipment"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white pt-32 pb-40">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              Join the
              <br />
              <span className="font-bold">Streak Team</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto font-light">
              Help us build the future of entrepreneurial success. 
              Every day, we're helping people turn ideas into revenue.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                View Open Positions
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-4 font-semibold backdrop-blur-sm">
                Learn About Culture
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">15+</div>
                <div className="text-black-400 text-sm">Team Members</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">100%</div>
                <div className="text-black-400 text-sm">Remote</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">4.9</div>
                <div className="text-black-400 text-sm">Team Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:border-gray-300 transition-colors">
                  <value.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-medium mb-4 text-black">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed font-light">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Open Positions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              Join us in building the future of entrepreneurial success
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {openPositions.map((position, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <Badge variant="secondary" className="text-xs">
                      {position.category}
                    </Badge>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{position.type}</div>
                      <div className="text-sm text-gray-500">{position.location}</div>
                    </div>
                  </div>
                  <CardTitle className="text-2xl mb-2 group-hover:text-blue-600 transition-colors">
                    {position.title}
                  </CardTitle>
                  <CardDescription className="text-base text-gray-600">
                    {position.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-600">{position.salary}</span>
                    </div>
                    <div className="text-sm text-gray-600 mb-4">
                      <strong>Requirements:</strong> {position.requirements.join(', ')}
                    </div>
                  </div>
                  <Button className="w-full group-hover:bg-blue-600 transition-colors">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Why Work With Us</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
              We take care of our team so you can focus on building amazing things
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="w-12 h-12 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:border-blue-300 transition-colors">
                  <Star className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-gray-700 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light mb-8">Our Culture</h2>
            <p className="text-xl text-blue-200 max-w-2xl mx-auto font-light">
              We're not just colleagues, we're a family of entrepreneurs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-light mb-4">Remote-First</h3>
              <p className="text-blue-200 leading-relaxed font-light">
                Work from anywhere in the world. We believe in results, not office hours.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-light mb-4">Growth Mindset</h3>
              <p className="text-blue-200 leading-relaxed font-light">
                Continuous learning and development. We invest in your success.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 border border-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-light mb-4">Impact-Driven</h3>
              <p className="text-blue-200 leading-relaxed font-light">
                Every feature we build helps real entrepreneurs succeed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Ready to Join the Streak?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-blue-200 font-light">
              Help us build the future of entrepreneurial success. 
              Your next adventure starts here.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                View All Positions
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-4 font-semibold backdrop-blur-sm">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
